// src/features/signature/RecordSignatureDialog.tsx
// Manually record a signature evidence record for a party — e.g. an
// offline/paper signature an admin is entering after the fact. Most
// signatures are created by provider adapters; this is the manual path.
// See docs/ux/signature-ia.md section 2.

import { useState } from 'react';
import { useI18n, useSignature } from '@react/hooks';
import { Button, Input, Textarea, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { SignatureMethodValues, SignatureLegalTierValues } from '@core/types/signature';
import type { Party, SignatureMethod, SignatureLegalTier } from '@core/types/signature';
import { enumSuffix } from './logic';

export function RecordSignatureDialog({ party, onSaved, onCancel }: { party: Party; onSaved: () => void; onCancel: () => void }) {
  const { t } = useI18n();
  const store = useSignature();
  const [method, setMethod] = useState<SignatureMethod>('SIGNATURE_METHOD_HANDWRITTEN_PAPER');
  const [legalTier, setLegalTier] = useState<SignatureLegalTier>('SIGNATURE_LEGAL_TIER_SIMPLE');
  const [contentHash, setContentHash] = useState('');
  const [note, setNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    try {
      await store.CreateSignature({
        signature: {
          entity: { type_id: '', type_name: 'signature', id: '', platform_id: '', tenant_id: '', product_id: '' },
          signed_hash_algo: 'sha256',
          content_hash: contentHash,
          document_version: 0,
          target: party.target,
          party_ref: party.entity,
          signer: party.party_actor,
          method,
          legal_tier: legalTier,
          evidence: { opaque: { payload: { note }, provider_slug: 'manual-entry', format_hint: 'text' } },
          provider_slug: 'manual-entry',
          cert_ref: '',
          ip_address: '',
          user_agent: '',
        },
      });
      onSaved();
    } catch (err) {
      setError(err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3 rounded-lg border border-border p-4">
      <h3 className="text-sm font-semibold text-foreground">{t('signature.parties.recordSignatureTitle')}</h3>
      {error != null && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}
      <div className="grid grid-cols-2 gap-3">
        <Select
          label={t('signature.field.method')}
          value={method}
          onChange={(e) => setMethod(e.target.value as SignatureMethod)}
          options={SignatureMethodValues.filter((v) => v !== 'SIGNATURE_METHOD_UNSPECIFIED').map((v) => ({ value: v, label: t(`signature.method.${enumSuffix(v, 'SIGNATURE_METHOD_')}`) }))}
        />
        <Select
          label={t('signature.field.legalTier')}
          value={legalTier}
          onChange={(e) => setLegalTier(e.target.value as SignatureLegalTier)}
          options={SignatureLegalTierValues.filter((v) => v !== 'SIGNATURE_LEGAL_TIER_UNSPECIFIED').map((v) => ({ value: v, label: t(`signature.legalTier.${enumSuffix(v, 'SIGNATURE_LEGAL_TIER_')}`) }))}
        />
      </div>
      <Input label={t('signature.field.contentHash')} value={contentHash} onChange={(e) => setContentHash(e.target.value)} required helperText={t('signature.field.contentHashHelp')} />
      <Textarea label={t('signature.field.note')} value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" disabled={isSaving}>{isSaving ? t('common.saving') : t('signature.parties.recordSignature')}</Button>
        <Button type="button" variant="secondary" size="sm" onClick={onCancel} disabled={isSaving}>{t('common.cancel')}</Button>
      </div>
    </form>
  );
}
